import { PageConfig, UserRole } from '@bco/types';
import {
  BascAlert,
  BascAppShell,
  BascAppShellFooter,
  BascAppShellHeader,
  BascAppShellMain,
  BascBox,
  BascBurger,
  BascButton,
  BascCenter,
  BascDatesProvider,
  BascDivider,
  BascDrawer,
  BascGroup,
  BascIcon,
  BascLanguageSelector,
  BascLoader,
  BascStack,
  BascText,
  CommonIcons,
  getAccessToken,
  getGatewayUrl,
  isCompanionWindowSearch,
  useAuthStore,
  useDisclosure,
  useEnvironment,
  useTranslation,
} from '@bco/ui';
import { ComponentType, Suspense, lazy, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { BottomNav } from './components/BottomNav';
import { ClientSelector } from './components/ClientSelector';
import { ClientThemeProvider } from './components/ClientThemeProvider';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { loadPageComponent, loadToolbarComponent } from './federationInit';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { SetupMfaPage } from './pages/SetupMfa';
import { VerifyEmailPage } from './pages/VerifyEmail';
import './shell.css';

declare const __APP_VERSION__: string;

interface User {
  email: string;
  role: UserRole;
  tenantId: string;
}

// Cache for lazy-loaded remote components
const componentCache = new Map<string, ComponentType>();

function getRemoteComponent(
  clientName: string,
  pageName: string
): ComponentType {
  const cacheKey = `pages/${clientName}/${pageName}`;

  if (componentCache.has(cacheKey)) {
    return componentCache.get(cacheKey)!;
  }

  const Component = lazy(async () => {
    const module = await loadPageComponent(clientName, pageName);
    return { default: module.default };
  });

  componentCache.set(cacheKey, Component);
  return Component;
}

function getToolbarComponent(
  clientName: string,
  slot: 'left' | 'center' | 'right' | 'drawer'
): ComponentType {
  const cacheKey = `toolbar/${clientName}/${slot}`;

  if (componentCache.has(cacheKey)) {
    return componentCache.get(cacheKey)!;
  }

  const Component = lazy(async () => {
    const module = await loadToolbarComponent(clientName, slot);
    return { default: module.default };
  });

  componentCache.set(cacheKey, Component);
  return Component;
}

function ToolbarSlot({
  clientName,
  slot,
}: {
  clientName: string;
  slot: 'left' | 'center' | 'right' | 'drawer';
}) {
  const Component = getToolbarComponent(clientName, slot);

  return (
    <Suspense fallback={null}>
      <Component />
    </Suspense>
  );
}

function Header({
  opened,
  toggle,
  clientName,
  pages,
  activeTab,
  onTabChange,
}: {
  opened: boolean;
  toggle: () => void;
  clientName: string;
  pages: PageConfig[];
  activeTab: string | null;
  onTabChange: (tab: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <BascGroup
      h="100%"
      px="md"
      justify="space-between"
      wrap="nowrap"
      className="shell-header"
    >
      <BascGroup wrap="nowrap">
        <BascBurger
          opened={opened}
          onClick={toggle}
          hiddenFrom="sm"
          size="sm"
          color="var(--header-text)"
          className="shell-header-burger"
          aria-label={t('app.navigation', 'Navigation')}
        />
        <ToolbarSlot clientName={clientName} slot="left" />
      </BascGroup>

      <BascGroup gap="xs" visibleFrom="sm">
        {pages.map((page) => (
          <BascButton
            key={page.id}
            variant={page.id === activeTab ? 'light' : 'subtle'}
            size="compact-sm"
            onClick={() => onTabChange(page.id)}
            className={`toolbar-nav-link${page.id === activeTab ? ' toolbar-nav-link-active' : ''}`}
          >
            {t(`pages.${page.id.toLowerCase()}`, page.label)}
          </BascButton>
        ))}
      </BascGroup>

      <BascGroup gap="sm" wrap="nowrap">
        <ClientSelector currentClientName={clientName} />
        <ToolbarSlot clientName={clientName} slot="right" />
      </BascGroup>
    </BascGroup>
  );
}

function PageLoader({
  clientName,
  pageName,
}: {
  clientName: string;
  pageName: string;
}) {
  const Component = getRemoteComponent(clientName, pageName);

  // Boundary wraps the Suspense-loaded remote so a render/commit throw in the
  // page shows a recovery fallback while the shell chrome survives. Keyed +
  // resetKeys'd by route so navigating to another page remounts/resets it.
  const routeKey = `${clientName}/${pageName}`;

  return (
    <PageErrorBoundary key={routeKey} resetKeys={[routeKey]}>
      <Suspense
        fallback={
          <BascCenter p="xl">
            <BascLoader size="lg" />
          </BascCenter>
        }
      >
        <Component />
      </Suspense>
    </PageErrorBoundary>
  );
}

const BUILTIN_PAGES = new Set(['profile']);

function NotFound() {
  const { t } = useTranslation();
  return (
    <BascCenter style={{ height: '100%' }}>
      <BascBox ta="center">
        <BascText textRole="heading2" fw={500} mb="xs">
          404
        </BascText>
        <BascText c="dimmed">{t('errors.notFound', 'Page not found')}</BascText>
      </BascBox>
    </BascCenter>
  );
}

const RECOVERY_LOGIN_FLAG = 'bco.recoveryLogin';
/** Per-session dismissal flag for the recovery banner. */
const RECOVERY_BANNER_DISMISSED_FLAG = 'bco.recoveryBannerDismissed';

/**
 * Persistent banner shown after a backup-code (recovery) sign-in. Prompts the
 * user to verify their authenticator still works and to regenerate codes.
 * Dismissible per session.
 */
function RecoveryBanner({ clientName }: { clientName: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(
    sessionStorage.getItem(RECOVERY_LOGIN_FLAG) === '1' &&
      sessionStorage.getItem(RECOVERY_BANNER_DISMISSED_FLAG) !== '1'
  );

  if (!visible) {
    return null;
  }

  const dismiss = () => {
    sessionStorage.setItem(RECOVERY_BANNER_DISMISSED_FLAG, '1');
    setVisible(false);
  };

  return (
    <BascAlert
      color="yellow"
      variant="light"
      icon={<BascIcon name={CommonIcons.WARNING} />}
      title={t('auth.mfa.recovery.bannerTitle')}
      withCloseButton
      onClose={dismiss}
      m="md"
    >
      <BascGroup justify="space-between" align="center" wrap="wrap" gap="sm">
        <BascText textRole="body">{t('auth.mfa.recovery.bannerBody')}</BascText>
        <BascButton
          size="xs"
          variant="filled"
          color="yellow"
          onClick={() => navigate(`/${clientName}/profile#security`)}
        >
          {t('auth.mfa.recovery.regenerate')}
        </BascButton>
      </BascGroup>
    </BascAlert>
  );
}

function MainContent({
  pages,
  activeTab,
  clientName,
}: {
  pages: PageConfig[];
  activeTab: string | null;
  clientName: string;
}) {
  const environment = useEnvironment();

  if (activeTab === 'system-testing' && environment === 'production') {
    return (
      <BascBox className="shell-content">
        <NotFound />
      </BascBox>
    );
  }

  if (activeTab && BUILTIN_PAGES.has(activeTab)) {
    const pageName = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
    return (
      <BascBox className="shell-content">
        <PageLoader clientName={clientName} pageName={pageName} />
      </BascBox>
    );
  }

  const activePage = pages.find((p) => p.id === activeTab);
  const pageName = activePage?.moduleName?.split('/').pop() || '';

  return (
    <BascBox className="shell-content">
      {activePage && pageName && (
        <PageLoader clientName={clientName} pageName={pageName} />
      )}
    </BascBox>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <BascGroup
      h="100%"
      px="md"
      justify="space-between"
      className="shell-footer"
    >
      <BascText textRole="body" c="inherit">
        {t('footer.copyright', { year: new Date().getFullYear() })} | v
        {__APP_VERSION__}
      </BascText>
      <BascGroup gap="md">
        <BascLanguageSelector compact />
        <BascText textRole="body" c="inherit">
          {t('footer.privacy')}
        </BascText>
        <BascText textRole="body" c="inherit">
          {t('footer.terms')}
        </BascText>
      </BascGroup>
    </BascGroup>
  );
}

async function fetchPages(
  clientName: string
): Promise<{ pages: PageConfig[]; defaultPageId: string }> {
  // Empty fallback ⇒ relative same-origin (`/api/...`) so requests hit the
  // page's own origin (Vite `/api` proxy on localhost, nginx on tunnel/prod).
  // An absolute base is used only when VITE_GATEWAY_URL is explicitly set (prod).
  const gatewayUrl = getGatewayUrl();
  const token = getAccessToken();

  if (!token) {
    throw new Error('No auth token');
  }

  const response = await fetch(`${gatewayUrl}/api/pages/${clientName}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch pages');
  }
  return response.json();
}

async function fetchUser(): Promise<User> {
  // Empty fallback ⇒ relative same-origin (see fetchPages note above).
  const gatewayUrl = getGatewayUrl();
  const token = getAccessToken();

  if (!token) {
    throw new Error('No auth token');
  }

  const response = await fetch(`${gatewayUrl}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user');
  }
  return response.json();
}

function ClientApp() {
  const { clientName, pageId } = useParams<{
    clientName: string;
    pageId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure();
  // A companion window is a bare second-screen document: it carries no
  // navigation of its own, so the shell contributes only the main region.
  const isCompanionWindow = isCompanionWindowSearch(location.search);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [pages, setPages] = useState<PageConfig[]>([]);
  const [_user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientName) {
      return;
    }

    async function init() {
      try {
        const [pagesData, userData] = await Promise.all([
          fetchPages(clientName!),
          fetchUser().catch(() => null),
        ]);

        setPages(pagesData.pages);
        setUser(userData);

        // Set active tab from URL or default
        const initialTab = pageId || pagesData.defaultPageId;
        setActiveTab(initialTab);

        // If no pageId in URL, redirect to default
        if (!pageId && pagesData.defaultPageId) {
          navigate(`/${clientName}/${pagesData.defaultPageId}`, {
            replace: true,
          });
        }
      } catch {
        // Error handled - loading state cleared in finally
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [clientName, pageId, navigate]);

  const handleTabChange = (tab: string | null) => {
    if (tab && clientName) {
      setActiveTab(tab);
      navigate(`/${clientName}/${tab}`);
    }
  };

  if (loading) {
    return (
      <BascCenter className="shell-loading">
        <BascLoader size="lg" />
      </BascCenter>
    );
  }

  if (!clientName) {
    return (
      <BascCenter className="shell-loading">
        <BascText>{t('errors.generic')}</BascText>
      </BascCenter>
    );
  }

  // Shared by both shells so a companion window cannot bypass the "no pages
  // granted" guard the chromed shell applies.
  const mainRegion =
    pages.length === 0 && !(activeTab && BUILTIN_PAGES.has(activeTab)) ? (
      <BascCenter style={{ height: '100%' }}>
        <BascBox ta="center">
          <BascText textRole="heading2" fw={500} mb="xs">
            {t('noData')}
          </BascText>
          <BascText c="dimmed">{t('errors.unauthorized')}</BascText>
        </BascBox>
      </BascCenter>
    ) : (
      <MainContent
        pages={pages}
        activeTab={activeTab}
        clientName={clientName}
      />
    );

  if (isCompanionWindow) {
    return (
      <BascAppShell padding="0">
        <BascAppShellMain className="shell-main">{mainRegion}</BascAppShellMain>
      </BascAppShell>
    );
  }

  return (
    <BascAppShell padding="0">
      <BascAppShellHeader>
        <Header
          opened={opened}
          toggle={toggle}
          clientName={clientName}
          pages={pages}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </BascAppShellHeader>

      <BascDrawer
        opened={opened}
        onClose={toggle}
        size="xs"
        title={t('app.navigation', 'Navigation')}
      >
        {pages.map((page) => (
          <BascButton
            key={page.id}
            variant={page.id === activeTab ? 'light' : 'subtle'}
            fullWidth
            justify="flex-start"
            mb="xs"
            onClick={() => {
              handleTabChange(page.id);
              toggle();
            }}
          >
            {t(`pages.${page.id.toLowerCase()}`, page.label)}
          </BascButton>
        ))}
        <BascDivider my="sm" />
        <ToolbarSlot clientName={clientName} slot="drawer" />
        {/* Footer language + legal relocate here on mobile (footer is
            visibleFrom="sm"), keeping them reachable without the desktop bar. */}
        <BascDivider my="sm" />
        <BascStack gap="sm" px="xs">
          <BascLanguageSelector />
          <BascGroup gap="md">
            <BascText textRole="body" c="dimmed">
              {t('footer.privacy')}
            </BascText>
            <BascText textRole="body" c="dimmed">
              {t('footer.terms')}
            </BascText>
          </BascGroup>
        </BascStack>
      </BascDrawer>

      <BascAppShellMain className="shell-main">
        <RecoveryBanner clientName={clientName} />
        {mainRegion}
      </BascAppShellMain>

      <BascAppShellFooter visibleFrom="sm">
        <Footer />
      </BascAppShellFooter>

      <BottomNav
        pages={pages}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onMore={toggle}
        moreOpened={opened}
      />
    </BascAppShell>
  );
}

function ClientAppWithTheme() {
  return (
    <ClientThemeProvider>
      <ClientApp />
    </ClientThemeProvider>
  );
}

function LoginPageWithTheme() {
  return (
    <ClientThemeProvider>
      <LoginPage />
    </ClientThemeProvider>
  );
}

function RegisterPageWithTheme() {
  return (
    <ClientThemeProvider>
      <RegisterPage />
    </ClientThemeProvider>
  );
}

function VerifyEmailPageWithTheme() {
  return (
    <ClientThemeProvider>
      <VerifyEmailPage />
    </ClientThemeProvider>
  );
}

function ForgotPasswordPageWithTheme() {
  return (
    <ClientThemeProvider>
      <ForgotPasswordPage />
    </ClientThemeProvider>
  );
}

function SetupMfaPageWithTheme() {
  return (
    <ClientThemeProvider>
      <SetupMfaPage />
    </ClientThemeProvider>
  );
}

/**
 * Auth guard - redirects to login if not authenticated
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { clientName } = useParams<{ clientName: string }>();
  const { isAuthenticated, hasHydrated } = useAuthStore();

  // Wait for store to hydrate from sessionStorage
  if (!hasHydrated) {
    return (
      <BascCenter style={{ height: '100vh' }}>
        <BascLoader size="lg" />
      </BascCenter>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/${clientName}/login`} replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BascDatesProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/:clientName/login" element={<LoginPageWithTheme />} />
        <Route
          path="/:clientName/register"
          element={<RegisterPageWithTheme />}
        />
        <Route
          path="/:clientName/verify-email"
          element={<VerifyEmailPageWithTheme />}
        />
        <Route
          path="/:clientName/forgot-password"
          element={<ForgotPasswordPageWithTheme />}
        />
        {/* Mandatory MFA enrollment interstitial. Registered BEFORE the
            catch-all `/:clientName/:pageId` and `/:clientName` routes. NOT
            wrapped in RequireAuth — it self-guards on the in-memory router
            state carrying the enrollment token. */}
        <Route
          path="/:clientName/setup-mfa"
          element={<SetupMfaPageWithTheme />}
        />

        {/* Protected routes */}
        <Route
          path="/:clientName/:pageId"
          element={
            <RequireAuth>
              <ClientAppWithTheme />
            </RequireAuth>
          }
        />
        <Route
          path="/:clientName"
          element={
            <RequireAuth>
              <ClientAppWithTheme />
            </RequireAuth>
          }
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={
            <Navigate
              to={`/${import.meta.env.VITE_DEFAULT_CLIENT || 'bconduct'}`}
              replace
            />
          }
        />
      </Routes>
    </BascDatesProvider>
  );
}
